// Copyright (c) 2025 Audere, All Rights Reserved

import type { Theme } from "@emotion/react";
import type { SxProps, InputBaseProps, ButtonProps } from "@mui/material";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { SerializedError } from "@reduxjs/toolkit";
import { t } from "i18next";

export interface ProjectBasic {
  uid: string;
  projectName: string;
}

export interface ProjectNameFormProps {
  projectData: ProjectBasic;
  handleStep: (step: number, data: ProjectBasic) => void;
  currentProjectData?: GetProjectData;
  onCancel: () => void;
  listProjectsData: ListProjectsData;
}

export interface ApiState {
  isError: boolean;
  error?: QueryError;
}

export interface AIModelSetupProps {
  projectDetails: ProjectBasic;
  handleStep: (step: number, data: ProjectBasic | undefined) => void;
  handleEditProject: (step: number) => void;
  onContinue: (selectedTest: TestItem[]) => void;
  error?: QueryError;
  onAddNewTest: () => void;
  setNewTests: Dispatch<SetStateAction<TestItem[]>>;
  newTests: TestItem[];
  currentProjectData?: GetProjectData;
  onCancel: () => void;
}

export interface ProjectDetailsProps {
  projectDetails: ProjectBasic;
  handleEditProject: () => void;
}

export interface SelectedTestIds {
  organization_uid: string;
  rdt_uid: string;
}

export interface TestItem extends SelectedTestIds {
  brand: string;
  model: string;
  condition: string;
  example_image_url?: string | null;
  last_modified?: string;
}

export interface TestItemProps {
  testItem: TestItem;
  toggleTestSelection: (checked: boolean) => void;
  isTestSelected: boolean;
}

export interface NewTestsProps {
  newTests: TestItem[];
  onAddNewTest: () => void;
  selectedTestIds: string[];
  onToggleNewTestSelection: (newSelectedIds: string[]) => void;
}

export interface ListRDTsData {
  rdts: TestItem[];
}

export interface AudereSupportedTestItems {
  condition: string;
  rdts: TestItem[];
}

export interface AudereSupportedTestsProps {
  audereSupportedTests: AudereSupportedTestItems[];
  selectedTestIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
}

export interface SearchInputProps {
  sx?: SxProps<Theme>;
  inputProps?: InputBaseProps;
}

export type LineTestType = (typeof TEST_TYPES)[keyof typeof TEST_TYPES];

export interface CreateNewTest {
  thumbNail: string;
  brandName: string;
  model: string;
  condition: string;
  lineTestType: LineTestType;
}

export interface addRDTPayload {
  brand: string;
  model: string;
  condition: string;
  who_product_name?: string | null;
  who_pq: string | null;
  results: {
    window0: TestResults;
  };
  lineTestType?: LineTestType;
}

export interface BottomBasketProps {
  testItems: TestItem[];
  onContinue: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onRemove: (id: string) => void;
  onCancel: () => void;
}

export interface BottomBasketContentProps {
  testsCount: number;
  onClick: () => void;
  onContinueClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  isOpen: boolean;
  onCancel: () => void;
}

export interface ApiErrorResponse {
  response_code: string;
  message: string;
  request_uid: string;
}

export type QueryError = FetchBaseQueryError | SerializedError | undefined;

export const ModelType = {
  RDT_SPECIFIC: "rdt_specific",
  RDT_GENERIC: "rdt_generic",
  COMBINED: "combined",
} as const;

export interface TestResults {
  control: string[];
  test0: string | string[];
  test1?: string | string[];
}

export interface AddNewTestFormProps {
  lineTestType: LineTestType;
  setLineTestType: (value: LineTestType) => void;
  onCancel: () => void;
}

export interface FileInfo {
  file: File | null;
  hash: string;
  filename: string;
  size: number;
  status: FileStatus;
  progress: number;
  error?: string;
  uploadId?: string;
  uploadURL?: string;
}

export interface VirtualizedFileListProps {
  files: FileInfo[];
  onRemoveFile: (file: FileInfo) => void;
  height?: number;
  onScrollToTop?: () => void;
}

export interface FileUploadAreaProps {
  setFiles: React.Dispatch<React.SetStateAction<FileInfo[]>>;
  setProcessing: React.Dispatch<React.SetStateAction<boolean>>;
}

export const FileStatus = {
  Pending: "pending",
  Checking: "checking",
  Exists: "exists",
  Uploading: "uploading",
  Completed: "completed",
  Error: "error",
  Invalid: "invalid",
} as const;

export type FileStatus = (typeof FileStatus)[keyof typeof FileStatus];

export const FileUploadApiStatus = {
  NEW: "NEW",
  UPLOADED: "UPLOADED",
} as const;

export type FileUploadApiStatusType =
  (typeof FileUploadApiStatus)[keyof typeof FileUploadApiStatus];

export const TEST_TYPES = {
  TWO_LINE: "2-line test",
  THREE_LINE: "3-line test",
} as const;

export const TestConditions = {
  HIV: t("AIModelSetup:testConditions:HIV"),
  Malaria: t("AIModelSetup:testConditions:Malaria"),
  FOUNDATION: t("AIModelSetup:testConditions:FOUNDATION"),
  Others: t("AIModelSetup:testConditions:Others"),
} as const;

export interface CreateProjectPayload {
  projectName: string;
  rdts: SelectedTestIds[];
  modelType: string;
}

export interface UpdateProjectPayload extends Partial<CreateProjectPayload> {
  projectUid: string;
}

export interface ProjectMutationResponse {
  project_uid: string;
}

export interface ProjectData {
  project_uid: string;
  name: string;
  number_rdts: number;
  last_modified: string;
}

export interface ListProjectsData {
  projects: ProjectData[];
}

export interface TestData extends SelectedTestIds {
  brand: string;
  model: string;
  condition: string;
  last_modified: string;
  who_product_name: string | null;
  who_pq: string | null;
  results: {
    [windowKey: string]: {
      [testKey: string]: string[];
    };
  };
  allow_sharing: boolean;
  example_image_url: string | null;
  image_assessment: {
    status: TestStatus;
  };
  image_labeling: {
    status: LabelingStatus;
  };
}

export interface TestListsItemProps {
  test: TestData;
  isSelected: boolean;
  onSelectTest: (test: TestData) => void;
  isCollapsed?: boolean;
}

export interface ProjectListMenuProps {
  startIcon: ReactNode;
  endIconOpen?: ReactNode;
  endIconClosed?: ReactNode;
  primaryText: string;
  secondaryText: string;
  menuItems: ProjectData[];
  onMenuItemClick: (item: ProjectData) => void;
  buttonProps: ButtonProps;
  isCollapsed?: boolean;
}

export interface ProjectModel {
  organization_uid: string;
  model_uid: string;
  model_version: string;
  last_modified: string;
  training?: ModelStatus;
  validation?: ModelStatus;
}
export interface GetProjectData {
  project: {
    project_uid: string;
    name: string;
    model_type: string;
    rdts: TestData[];
    models: ProjectModel[];
    debug: boolean;
    raw_results: boolean;
  };
}

export interface GetProjectPayload {
  projectUid: string;
}

export interface ProjectItemsProps {
  projects: ProjectData[];
  onProjectSelect: (projectUid: string) => void;
}

export const PROJECT_FLOW_QUERY_KEYS = {
  STEP: "step",
} as const;

export const PROJECT_FLOW_STEP_VALUES = {
  PROJECT_NAME: "project-name",
  AI_MODEL_SETUP: "ai-model-setup",
  ADD_NEW_TESTS: "add-new-tests",
  IMAGE_UPLOAD: "image-upload",
};

export const stepToQueryValue = {
  0: PROJECT_FLOW_STEP_VALUES.PROJECT_NAME,
  1: PROJECT_FLOW_STEP_VALUES.AI_MODEL_SETUP,
} as const;

export type StepType = keyof typeof stepToQueryValue;

export interface ProjectState {
  projectData: ProjectBasic;
  selectedTestIds: string[];
  newTestFormValues: addRDTPayload | undefined;
}

export interface FileInputProps {
  value: File | null | string;
  onFileInputChange: (file: File | null) => void;
}

export interface addRDTResponse {
  rdt_uid: string;
}

export interface uploadRdtExamplePhotoResponse {
  upload_url: string;
}

export interface uploadRdtExamplePhotoPayload {
  rdt_uid: string;
}

export interface Step {
  label: string;
  status: "completed" | "inProgress" | "notStarted" | string;
}

export interface TestUploadImageProgressProps {
  testData: TestData;
  onProceed: (rdt_uid: string) => void;
  onAddMoreImages: (testId: string) => void;
}

export interface InfoBannerProps {
  icon: ReactNode;
  message: string;
  hasError?: boolean;
}

export interface TabSwitcherProps {
  activeTab: number;
  setActiveTab: React.Dispatch<React.SetStateAction<number>>;
}

export const LABELING_STATUS = {
  NOT_STARTED: "NOT_STARTED",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETE: "COMPLETE",
} as const;

export type LabelingStatus = (typeof LABELING_STATUS)[keyof typeof LABELING_STATUS];

export const TEST_STATUS = {
  NOT_AVAILABLE: "NOT_AVAILABLE",
  IN_PROGRESS: "IN_PROGRESS",
  SUCCESS: "SUCCESS",
  FAILURE: "FAILURE",
} as const;

export type TestStatus = (typeof TEST_STATUS)[keyof typeof TEST_STATUS];
export interface RDTStatus extends TestItem {
  organization_uid: string;
  rdt_uid: string;
  image_analysis_status: TestStatus;
  labeling_status: LabelingStatus;
}
export interface Model {
  organization_uid: string;
  model_uid: string;
  model_version: string;
  last_modified: string;
  training?: ModelStatus;
  validation?: ModelStatus;
}
export interface ModelStatus {
  status: TestStatus;
  start_timestamp?: string;
  end_timestamp?: string;
  error?: {
    message: string;
  };
}

export interface ProjectStatusResponse {
  project_uid: string;
  rdts: RDTStatus[];
  models: ModelStatus[];
}

export interface TestItemWithStatus extends TestItem {
  image_analysis_status: string;
  labeling_status: string;
}

export type GetStatusIcon =
  | "SUCCESS"
  | "COMPLETE"
  | "IN_PROGRESS"
  | "NOT_AVAILABLE"
  | "FAILURE"
  | "NOT_STARTED";

export interface WeightedF1ConfidenceInterval {
  low: number;
  high: number;
}

export interface FilteredTrueOutcomeDistribution {
  num_photos: Record<string, number>;
  percent_photos: Record<string, number>;
}

export interface RdtBrandDetected {
  brand: string;
  model: string;
  condition: string;
  n: number;
}

export interface FilterResults {
  [key: string]: { n: number };
}

export interface AllReportCard {
  total_images: number;
  total_images_passing_filters: number;
  percent_images_failing_filters: number | null;
  weighted_recall: number | null;
  weighted_precision: number | null;
  weighted_f1: number | null;
  weighted_f1_confidence_interval: WeightedF1ConfidenceInterval | null;
  total_regular_positives: number;
  regular_positive_accuracy: number | null;
  total_faint_positives: number;
  faint_positive_accuracy: number | null;
  sensitivity: number | null;
  filtered_confusion_matrix: FilteredConfusionMatrix;
  filtered_true_outcome_distribution: FilteredTrueOutcomeDistribution;
  rdt_brand_detected_distribution: RdtBrandDetected[];
  filter_results: FilterResults;
}

export interface ReportCards {
  [key: string]: AllReportCard;
}

export interface ReportCardResponse {
  report_cards: ReportCards;
}
export interface PredictionData {
  pred_invalid: number;
  pred_negative: number;
  pred_positive: number;
  pred_cant_tell: number;
}

export interface FilteredConfusionMatrix {
  true_invalid: PredictionData;
  true_negative: PredictionData;
  true_positive: PredictionData;
  true_cant_tell: PredictionData;
}

export interface ConfusionMatrixProps {
  confusionMatrixData: FilteredConfusionMatrix;
}

export interface ReportsHeaderProps {
  selectedTest: SelectedTestData;
  outcomeDistribution: TableRow[];
  dropdownOptions: DropdownOption[];
  setSelectedOption: Dispatch<SetStateAction<string>>;
  selectedOption: string;
  handleRDTSelection: (rdt_uid: string) => void;
}

export interface ReportsContainerProps {
  selectedTest?: SelectedTestData;
  handleRDTSelection: (rdt_uid: string) => void;
  latestModel?: ProjectModel | null;
}

export type TableColumn = { header: string; key: string };
export type TableRow = Record<string, string | number>;

export const tableColumn = (header: string, key: string): TableColumn => ({ header, key });

export interface OutcomeItem {
  label: string;
  value?: string;
  percentage: string;
}

export interface SelectedTestData {
  models: ProjectModel[];
  rdt: TestData;
  name: string;
  projectUid: string;
}

export interface DashboardSidebarProps {
  onSelectRDTTest: (value: TestData, projectData: GetProjectData) => void;
  isBelowTabletSize?: boolean;
  isDrawerOpen?: boolean;
  onDrawerToggle?: () => void;
  getProjectData?: GetProjectData;
  selectedProject?: ProjectData;
  setSelectedProject: Dispatch<SetStateAction<ProjectData | undefined>>;
  selectedTest?: TestData;
  setSelectedTest: Dispatch<SetStateAction<TestData | undefined>>;
  listProjectsData?: ListProjectsData;
}

export interface ReportCardPayload {
  organization_uid?: string;
  project_uid?: string;
  rdt: {
    organization_uid?: string;
    rdt_uid?: string;
  };
  model: {
    organization_uid?: string;
    model_uid?: string;
  };
  photoset: {
    organization_uid?: string;
    photoset_uid?: string;
  };
}

export type TuningOptionValue =
  | "increaseAccuracy"
  | "reduceFalseNegatives"
  | "reduceFalsePositives";

export interface FurtherTuningDialogProps {
  projectName: string;
  isOpen: boolean;
  onClose: () => void;
  initialSelected?: TuningOptionValue[];
}

export interface FurtherTuningOption {
  labelKey: TuningOptionValue;
  descKey?: string;
}

export type FilteredConfusionMatrixKey = keyof FilteredConfusionMatrix;
export type PredictionDataKey = keyof PredictionData;

export interface DropdownOption {
  label: string;
  value: string;
}
export interface CreatePhotosetPayload {
  projectUid: string | null;
  rdtUid: string | null;
  purpose: string;
}

export interface CreatePhotosetResponse {
  photoset_uid: string;
}

export type ListPhotosetsPayload =
  | { projectUid: string; rdtUid?: never }
  | { rdtUid: string; projectUid?: never };

export interface PhotosetData {
  organization_uid: string;
  photoset_uid: string;
  project_uid: string | null;
  rdt_uid: string | null;
  purpose: string;
}

export interface ListPhotosetsResponse {
  photosets: PhotosetData[];
}

export const PHOTOSET_PURPOSE = {
  DEVELOPMENT: "DEVELOPMENT",
  MONITORING: "MONITORING",
} as const;

export type FileUploadAreaRef = {
  clearFiles: () => void;
};
export interface AllowSharingPayload {
  organization_uid: string;
  project_uid: string;
}

export interface AllowSharingDialogProps {
  open: boolean;
  onClose: () => void;
  onHandleClick: () => void;
}

export interface UpdateRDTResponse {
  rdt_uid: string;
}

export interface UpdateRDTPayload {
  rdt_uid: string;
  allow_sharing: boolean;
}

export interface GenerateSDkDialogProps {
  open: boolean;
  onClose: () => void;
}

export interface SdkApkDownloadDialogProps {
  open: boolean;
  onClose: () => void;
  onDownloadSdk: () => void;
}

export interface DownloadedApplication {
  name: string;
  package_name: string;
  edit_ts: string | null;
  version: string;
  build_uid: string;
  start_ts: string | null;
  end_ts: string | null;
  status: string;
}

export interface ManageSdkTabsProps {
  previousApps: Application[];
  onAppSelect: (app: Application) => void;
}

export interface ListApplicationsPayload {
  projectUid: string;
}

export type BuildState = "NOT_STARTED" | "IN_PROGRESS" | "SUCCESS" | "FAILURE";

export interface BuildStatus {
  state: BuildState;
  start_timestamp: string;
  end_timestamp: string;
  error: string | null;
}

export interface LatestBuild {
  organization_uid: string;
  build_uid: string;
  application_uid: string;
  model_uid: string;
  build_version: string;
  is_debug: boolean;
  build_status: BuildStatus;
}

export interface BuildInfo {
  organization_uid: string;
  app_build_uid: string;
  application_uid: string;
  model_uid: string;
  build_version: string;
  is_debug: boolean;
  build_status: BuildStatus;
}

export interface Application {
  organization_uid: string;
  application_uid: string;
  project_uid: string;
  name: string;
  android_app_id: string;
  last_updated: string;
  latest_successful_build: LatestBuild;
}

export interface ListApplicationsResponse {
  applications: Application[];
}

export interface ListAppBuildsPayload {
  applicationUid: string;
}

export interface ListAppBuildsResponse {
  app_builds: BuildInfo[];
}

export interface DownloadSdkPayload {
  buildUid: string;
}

export interface DownloadSdkResponse {
  download_urls: {
    organization_uid: string;
    app_build_uid: string;
    sdk_zip: string;
  };
}

export interface CreateApplicationPayload {
  projectUid: string;
  appName: string;
  appId: string;
}

export interface CreateApplicationResponse {
  organization_uid: string;
  application_uid: string;
}

export interface BuildApplicationPayload {
  applicationUid: string;
}

export interface BuildApplicationResponse {
  app_build: BuildInfo;
}

export type DashboardMenuOption =
  (typeof import("../../i18n/en.json"))["DashboardSidebar"]["menuOptions"][number];

export interface TestFileUploadProps {
  isFieldEvaluation?: boolean;
  onContinue?: () => void;
  onCancel?: () => void;
}

export interface FieldEvaluationProps {
  selectedTest?: SelectedTestData;
}

export interface EvaluationSetupProps {
  projectDetails: GetProjectData;
  latestModel?: ProjectModel | null;
}

export interface TestFileUploadProps {
  isThumbnailRequired?: boolean;
  onContinue?: () => void;
}
